import {
  EmptyState,
  EmptyStateContainer,
} from 'module/ui/components/atoms/empty-state';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { Button, Loader } from '@rocketlane/ui';
import * as docx from 'docx-preview';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const DocxPreviewWrapper = styled.div`
  width: 100%;
  min-height: 400px;
  padding: var(--spacing-05);

  .rl-docx-wrapper {
    font-family: inherit;
    background-color: transparent;
    padding: 0;
    .rl-docx {
      margin: 0;
      box-shadow: none;
    }
  }
`;

export const isDocxFile = (fileName: string): boolean =>
  fileName.toLowerCase().endsWith('.docx');

interface DocxPreviewViewerProps {
  fileUrl: string;
  fileName: string;
  onDownload: () => void;
}

export const DocxPreviewViewer: React.FC<DocxPreviewViewerProps> = ({
  fileUrl,
  fileName: _fileName,
  onDownload,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let cancelled = false;

    const loadAndRender = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(fileUrl);

        if (!response.ok) {
          throw new Error(`Failed to load file: ${response.status}`);
        }

        const blob = await response.blob();

        if (cancelled || !containerRef.current) {
          return;
        }

        containerRef.current.innerHTML = '';
        await docx.renderAsync(blob, containerRef.current, undefined, {
          className: 'rl-docx',
        });
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to preview document',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAndRender();

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  if (error) {
    return (
      <EmptyStateContainer>
        <EmptyState
          title={
            <FormattedMessage id="knowledge_base.file_viewer.could_not_preview_document" />
          }
          message={error}
          primaryCTA={
            <Button variant="primary" onClick={onDownload}>
              <FontAwesomeIcon
                icon={faDownload}
                style={{ marginRight: '8px' }}
              />
              <FormattedMessage id="knowledge_base.file_viewer.download_file" />
            </Button>
          }
        />
      </EmptyStateContainer>
    );
  }

  return (
    <DocxPreviewWrapper>
      {loading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
            color: 'var(--text-secondary)',
          }}
        >
          <Loader size={32} />
        </div>
      )}
      <div ref={containerRef} />
    </DocxPreviewWrapper>
  );
};
